import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { endOfMonth, startOfMonth } from 'date-fns';
import { IProperty } from '../../features/commons/properties/models/property.model';
import { PERIOD_PRESET } from '../../features/commons/stats-revenue/constants/presets';
import {
  CollectedType,
  StatsChartState,
  StatsFiltersResponse,
  StatsFlat,
  StatsForm,
  StatsResponse,
  StatsSettings,
  StatsSplitBy,
  StatsTypeOption,
} from '../../models';
import { StatsComparePeriod } from '../../models/objects/stats-compare-period';
import { StatsFiltersOptions } from '../../models/objects/stats-filters-options';

export const featureAdapter: EntityAdapter<StatsFlat> =
  createEntityAdapter<StatsFlat>({
    selectId: (model) => model.index,
  });

export const DEFAULT_FILTERS_VALUE: Partial<StatsForm> = {
  dates: [startOfMonth(new Date()), endOfMonth(new Date())],
  partitioning: 'monthly',
  compare: 'production_type',
  compare_year: null,
  year_on_year_flag: null,
  pickup: null,
  periodSelected: 'current_year',
  type: 'date',
  filters: {
    reservation_channel: null,
    accommodation: null,
    daily_rateplan: null,
    reservation_from: null,
    booker_category: null,
    bill_type: '1',
  },
};
export interface State extends EntityState<StatsFlat> {
  collectedOptions: StatsTypeOption[];
  typeOptions: { value: StatsSplitBy; label: string }[];
  compareOptions: { value: StatsSplitBy; label: string }[];
  selectedColumns: string[];
  isLoading?: boolean;
  isLoadingTableauNumbers: boolean;
  error?: any;
  settings?: StatsSettings[];
  stats: Partial<StatsResponse>;
  filtersOptionsByProperties: StatsFiltersResponse;
  filtersOptions: StatsFiltersOptions;
  properties: IProperty[];
  propertiesSelectedIds: number[];
  comparePeriod: StatsComparePeriod;
  enableValueChangeFilters: boolean;
  filtersHeaderValues: Partial<StatsForm>;
  min_date: Date;
  chart: StatsChartState;
  tableauNumbers: {
    accommodationRowsInViewport: StatsFlat[];
    accommodationRowsLoaded: StatsFlat[];
    accommodationRowsToLoad: StatsFlat[];
  };
  columnsLoaded: CollectedType[];
  splitLoading: boolean;
  totalLoading: boolean;
  totalsRow: StatsFlat;
  interruptCalls: boolean;
  exportId: number;
}

export const initialState: State = featureAdapter.getInitialState({
  collectedOptions: [
    {
      value: 'arrivals',
      label: 'arrivals',
      defaultColumn: true,
    },
    {
      value: 'departures',
      label: 'departures',
      defaultColumn: true,
    },
    {
      value: 'total_price',
      label: 'selling',
      defaultColumn: true,
    },
    {
      value: 'share_percent',
      label: 'incidence_percentage',
      defaultColumn: true,
    },
    {
      value: 'occupation_percentage',
      label: 'occupation_percentage',
      defaultColumn: true,
    },
    {
      value: 'adr',
      label: 'adr',
      defaultColumn: true,
    },
    {
      value: 'revpar',
      label: 'revpar',
      defaultColumn: true,
    },
    {
      value: 'sold_nights',
      label: 'sold_nights',
      defaultColumn: false,
    },
    {
      value: 'unsold_nights',
      label: 'unsold_nights',
      defaultColumn: false,
    },
    {
      value: 'counted_reservations',
      label: 'counted_reservations',
      defaultColumn: true,
    },
    {
      value: 'cancelled_reservations',
      label: 'cancelled_reservations',
      defaultColumn: false,
    },
    {
      value: 'cancellation_ratio',
      label: 'cancellation_ratio',
      defaultColumn: false,
    },
    {
      value: 'confirmed_reservations',
      label: 'confirmed_reservations',
      defaultColumn: false,
    },
    {
      value: 'average_stay_length',
      label: 'average_stay_length',
      defaultColumn: true,
    },
    {
      value: 'confirmed_average_booking_window',
      label: 'confirmed_average_booking_window',
      defaultColumn: false,
    },
    {
      value: 'cancelled_average_booking_window',
      label: 'cancelled_average_booking_window',
      defaultColumn: false,
    },
    {
      label: 'in_home',
      value: 'in_stay_guests',
      defaultColumn: false,
    },
    {
      label: 'attendances',
      value: 'presence_guests',
      defaultColumn: false,
    },
    {
      label: 'guests',
      value: 'absolute_guests',
      defaultColumn: false,
    },
    {
      label: 'quantity',
      value: 'quantity',
      defaultColumn: false,
    },
  ],
  typeOptions: [
    {
      value: 'date',
      label: 'period',
    },
    {
      value: 'production_type',
      label: 'department',
    },
    {
      value: 'service',
      label: 'service',
    },
    {
      value: 'channel',
      label: 'channel',
    },
    {
      value: 'accommodation',
      label: 'accommodation',
    },
    {
      value: 'rateplan',
      label: 'rate',
    },
    {
      value: 'nationality',
      label: 'nationality',
    },
    {
      value: 'reservation_from',
      label: 'market',
    },
    {
      value: 'booker_category',
      label: 'customer_type',
    },
    {
      value: 'dealer_agency',
      label: 'agency',
    },
    {
      value: 'dealer_company',
      label: 'company',
    },
    {
      value: 'dealer_tour_operator',
      label: 'tour_operator',
    },
    {
      value: 'reservation_reason',
      label: 'booking_reason',
    },
  ],
  compareOptions: [
    {
      value: 'rateplan',
      label: 'rate',
    },
    {
      value: 'nationality',
      label: 'nationality',
    },
    {
      value: 'reservation_from',
      label: 'market',
    },
    {
      value: 'booker_category',
      label: 'customer_type',
    },
    {
      value: 'dealer_agency',
      label: 'agency',
    },
    {
      value: 'dealer_company',
      label: 'company',
    },
    {
      value: 'dealer_tour_operator',
      label: 'tour_operator',
    },
    {
      value: 'date',
      label: 'period',
    },
    {
      value: 'reservation_reason',
      label: 'booking_reason',
    },
  ],
  selectedColumns: null,
  isLoading: false,
  isLoadingTableauNumbers: false,
  error: null,
  stats: {
    dictionary: {},
    values: [],
    totals: null,
  },
  filtersOptionsByProperties: null,
  filtersOptions: null,
  properties: null,
  min_date: null,
  propertiesSelectedIds: null,
  comparePeriod: {
    comparePeriodOptions: null,
    minPropertyActivationDate: null,
    periodPreset: PERIOD_PRESET,
    pickupPreset: PERIOD_PRESET,
    defaultPreset: PERIOD_PRESET,
  },
  chart: {
    chartTypeSelected: 'total_price',
  },
  enableValueChangeFilters: false,
  filtersHeaderValues: DEFAULT_FILTERS_VALUE,
  tableauNumbers: {
    accommodationRowsInViewport: [],
    accommodationRowsLoaded: [],
    accommodationRowsToLoad: [],
  },
  columnsLoaded: [],
  splitLoading: false,
  totalLoading: false,
  totalsRow: null,
  interruptCalls: false,
  exportId: null,
});
